import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import {
  getActivityFrequencyLabel,
  getActivityStyleLabel,
  getAgeGroupLabel,
} from '../utils/recruitmentLabels';
import { showToast } from '../utils/toast';
import CirclePostList from './circle/CirclePostList';
import CircleMemberList from './circle/CircleMemberList';
import CircleEventList from './circle/CircleEventList';
import './CircleManage.css';

const CULTURE_LABELS = {
  beginner_welcome: '初参加歓迎',
  no_pressure: '無理をしない',
  slow_pace: 'ゆっくり歩く',
  photo_friendly: '写真も楽しむ',
  food_after_hike: '下山後ごはん',
  retreat_positive: '撤退判断を大切にする',
  family_friendly: '家族参加歓迎',
  training: '体力づくり',
};

const SAFETY_LABELS = {
  submit_plan: '登山計画書の提出を推奨',
  gear_check: '装備チェック',
  weather_cancel: '悪天候時は中止・延期',
  pace_control: '参加者のペース優先',
  emergency_contact: '緊急連絡先を確認',
  insurance: '山岳保険加入を推奨',
};

function getDateTime(value) {
  if (!value) return '';
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' });
}

function countByStatus(applications, status) {
  return applications.filter((application) => (application.applicationStatus || 'pending') === status).length;
}

function CircleManage() {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [recruitment, setRecruitment] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dissolving, setDissolving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    async function loadCircle() {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      setLoading(true);
      setError('');

      try {
        const recruitmentSnap = await getDoc(doc(db, 'recruitments', id));
        if (!recruitmentSnap.exists()) {
          setError('サークルが見つかりませんでした。');
          return;
        }

        const data = { id: recruitmentSnap.id, ...recruitmentSnap.data() };
        if (data.recruitmentType !== 'group') {
          setError('この管理画面は所属メンバー募集のサークルのみ対応しています。');
          return;
        }

        if (data.userId !== currentUser.uid) {
          setError('このサークルを管理する権限がありません。');
          return;
        }

        const applicationsSnap = await getDocs(
          query(collection(db, 'applications'), where('recruitmentId', '==', id))
        );

        if (!active) return;
        setRecruitment(data);
        setApplications(applicationsSnap.docs.map((snap) => ({ id: snap.id, ...snap.data() })));
      } catch (loadError) {
        console.error('Circle manage load failed:', loadError);
        if (active) setError('サークル管理情報の取得に失敗しました。');
      } finally {
        if (active) setLoading(false);
      }
    }

    loadCircle();
    return () => { active = false; };
  }, [currentUser, id, navigate]);

  const stats = useMemo(() => ({
    pending: countByStatus(applications, 'pending'),
    approved: countByStatus(applications, 'approved'),
    rejected: countByStatus(applications, 'rejected'),
    total: applications.length,
  }), [applications]);

  const activeRate = stats.total ? Math.round((stats.approved / stats.total) * 100) : 0;
  const cultures = Array.isArray(recruitment?.circleCultures) ? recruitment.circleCultures : [];
  const safetyPolicies = Array.isArray(recruitment?.circleSafetyPolicies) ? recruitment.circleSafetyPolicies : [];

  const handleDissolve = async () => {
    if (!recruitment) return;
    if (!window.confirm(`「${recruitment.groupName}」のサークルを解散しますか？\n\n応募・チャットのデータも削除されます。この操作は元に戻せません。`)) return;
    setDissolving(true);
    try {
      const functions = getFunctions(undefined, 'asia-northeast1');
      const deleteRecruitmentFn = httpsCallable(functions, 'deleteRecruitment');
      await deleteRecruitmentFn({ recruitmentId: recruitment.id });
      showToast({ message: 'サークルを解散しました。', type: 'success' });
      navigate('/create-circle');
    } catch (dissolveError) {
      console.error('Circle dissolve failed:', dissolveError);
      showToast({ message: 'サークルの解散に失敗しました。時間をおいて再度お試しください。', type: 'error' });
    } finally {
      setDissolving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!recruitment) return;
    const nextStatus = recruitment.status === 'active' ? 'closed' : 'active';
    setSaving(true);
    try {
      await updateDoc(doc(db, 'recruitments', recruitment.id), {
        status: nextStatus,
        updatedAt: serverTimestamp(),
      });
      setRecruitment((prev) => ({ ...prev, status: nextStatus }));
      showToast({
        message: nextStatus === 'active' ? '募集を再開しました。' : '募集を停止しました。',
        type: 'success',
      });
    } catch (statusError) {
      console.error('Circle status update failed:', statusError);
      showToast({ message: '募集ステータスの更新に失敗しました。', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">読み込み中...</div>;

  if (error) {
    return (
      <div className="circle-manage-page">
        <div className="circle-manage-error">
          <h1>管理画面を表示できません</h1>
          <p>{error}</p>
          <div className="circle-manage-error-actions">
            <Link to="/create-circle" className="circle-manage-btn circle-manage-btn--primary">
              新しいサークルを作成する
            </Link>
            <Link to="/my-recruitments" className="circle-manage-btn circle-manage-btn--ghost">
              募集履歴へ戻る
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="circle-manage-page">
      <section className="circle-manage-hero">
        <div className="circle-manage-hero__image">
          {recruitment.imageUrl ? (
            <img src={recruitment.imageUrl} alt={`${recruitment.groupName}のカバー画像`} />
          ) : (
            <div className="circle-manage-hero__placeholder">Yamatch Circle</div>
          )}
        </div>
        <div className="circle-manage-hero__body">
          <p className="circle-manage-kicker">Circle Admin</p>
          <div className="circle-manage-title-row">
            <h1>{recruitment.groupName}</h1>
            <span className={`circle-manage-status circle-manage-status--${recruitment.status}`}>
              {recruitment.status === 'active' ? '募集中' : '停止中'}
            </span>
          </div>
          {recruitment.circleTagline && <p className="circle-manage-tagline">{recruitment.circleTagline}</p>}
          <div className="circle-manage-actions">
            <Link to={`/recruitments/${recruitment.id}`} className="circle-manage-btn circle-manage-btn--primary">
              募集ページを開く
            </Link>
            <Link to={`/edit-recruitment/${recruitment.id}`} className="circle-manage-btn circle-manage-btn--ghost">
              募集内容を編集
            </Link>
            <button
              type="button"
              className="circle-manage-btn circle-manage-btn--ghost"
              onClick={handleToggleStatus}
              disabled={saving || dissolving}
            >
              {saving ? '更新中...' : recruitment.status === 'active' ? '募集を停止' : '募集を再開'}
            </button>
            <button
              type="button"
              className="circle-manage-btn circle-manage-btn--danger"
              onClick={handleDissolve}
              disabled={saving || dissolving}
            >
              {dissolving ? '解散中...' : 'サークルを解散する'}
            </button>
          </div>
        </div>
      </section>

      <section className="circle-manage-stats" aria-label="応募状況">
        <div>
          <span>応募合計</span>
          <strong>{stats.total}</strong>
        </div>
        <div>
          <span>承認待ち</span>
          <strong>{stats.pending}</strong>
        </div>
        <div>
          <span>承認済み</span>
          <strong>{stats.approved}</strong>
        </div>
        <div>
          <span>承認率</span>
          <strong>{activeRate}%</strong>
        </div>
      </section>

      <div className="circle-manage-layout">
        <main className="circle-manage-main">
          <section className="circle-manage-section">
            <div className="circle-manage-section__head">
              <h2>サークル情報</h2>
              <span>公開ページに表示される基本情報</span>
            </div>
            <div className="circle-manage-info-grid">
              <Info label="活動地域" value={recruitment.prefecture} />
              <Info label="活動エリア" value={recruitment.activityArea} />
              <Info label="集合場所" value={recruitment.meetingPlace} />
              <Info label="活動頻度" value={getActivityFrequencyLabel(recruitment.activityFrequency, recruitment.activityFrequencyOther)} />
              <Info label="活動曜日・時間帯" value={recruitment.activityDays} />
              <Info label="メンバー数" value={recruitment.currentMembers ? `${recruitment.currentMembers}人` : ''} />
              <Info label="男女比" value={recruitment.genderRatio} />
              <Info label="年齢層" value={getAgeGroupLabel(recruitment.ageGroup)} />
              <Info label="活動スタイル" value={getActivityStyleLabel(recruitment.activityStyle)} />
              <Info label="作成日" value={getDateTime(recruitment.createdAt)} />
            </div>
          </section>

          <section className="circle-manage-section">
            <div className="circle-manage-section__head">
              <h2>参加案内</h2>
              <span>応募前の不安を減らす情報</span>
            </div>
            <TextBlock label="募集対象・参加条件" value={recruitment.memberRequirements} />
            <TextBlock label="参加までの流れ" value={recruitment.participationFlow} />
            <TextBlock label="費用・会費" value={recruitment.feeInfo} />
          </section>

          <section className="circle-manage-section">
            <div className="circle-manage-section__head">
              <h2>イベント</h2>
              <span>山行計画を投稿してメンバーを集めましょう</span>
            </div>
            <CircleEventList circleId={recruitment.id} isOwner />
          </section>

          <section className="circle-manage-section">
            <div className="circle-manage-section__head">
              <h2>活動記録</h2>
              <span>山行後に記録を残してメンバーを増やしましょう</span>
            </div>
            <CirclePostList circleId={recruitment.id} isOwner />
          </section>

          <section className="circle-manage-section">
            <div className="circle-manage-section__head">
              <h2>メンバー</h2>
              <span>退会・ロール変更はここから操作します</span>
            </div>
            <CircleMemberList circleId={recruitment.id} isOwner />
          </section>

          <section className="circle-manage-section">
            <div className="circle-manage-section__head">
              <h2>応募者</h2>
              <span>承認やチャットは募集ページで操作します</span>
            </div>
            {applications.length ? (
              <div className="circle-manage-applicants">
                {applications.slice(0, 8).map((application) => (
                  <div key={application.id} className="circle-manage-applicant">
                    <div>
                      <strong>{application.applicant?.nickname || application.applicantName || '応募者'}</strong>
                      <span>{getDateTime(application.createdAt) || '応募日未設定'}</span>
                    </div>
                    <em className={`circle-manage-applicant__status circle-manage-applicant__status--${application.applicationStatus || 'pending'}`}>
                      {application.applicationStatus === 'approved' ? '承認済み' : application.applicationStatus === 'rejected' ? '拒否済み' : '承認待ち'}
                    </em>
                  </div>
                ))}
              </div>
            ) : (
              <p className="circle-manage-empty">まだ応募はありません。募集ページを共有して参加希望者を集めましょう。</p>
            )}
            <Link to={`/recruitments/${recruitment.id}`} className="circle-manage-text-link">
              応募者管理を開く
            </Link>
          </section>
        </main>

        <aside className="circle-manage-side">
          <section className="circle-manage-section">
            <h2>サークルの文化</h2>
            <ChipList items={cultures.map((item) => CULTURE_LABELS[item] || item)} empty="未設定" />
          </section>

          <section className="circle-manage-section">
            <h2>安全方針</h2>
            <ChipList items={safetyPolicies.map((item) => SAFETY_LABELS[item] || item)} empty="未設定" safety />
          </section>

          <section className="circle-manage-section circle-manage-next">
            <h2>次にやること</h2>
            <ul>
              <li>募集ページを確認する</li>
              <li>紹介文に初参加の流れを書く</li>
              <li>応募が来たら早めに返信する</li>
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="circle-manage-info">
      <span>{label}</span>
      <strong>{value || '未設定'}</strong>
    </div>
  );
}

function TextBlock({ label, value }) {
  return (
    <div className="circle-manage-text-block">
      <span>{label}</span>
      <p>{value || '未設定'}</p>
    </div>
  );
}

function ChipList({ items, empty, safety }) {
  if (!items.length) return <p className="circle-manage-empty">{empty}</p>;
  return (
    <div className={`circle-manage-chips${safety ? ' circle-manage-chips--safety' : ''}`}>
      {items.map((item) => <span key={item}>{item}</span>)}
    </div>
  );
}

export default CircleManage;
